import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const CUES = ["INIT SEQUENCE", "PATCHING FIXTURES", "SETTING BEAMS", "STAND BY", "GO"];

const Letter = ({ ch, i }) => (
  <motion.span
    initial={{ opacity: 0, y: 40, filter: "blur(12px)" }}
    animate={{
      opacity: [0, 1, 0.7, 1],
      y: 0,
      filter: ["blur(12px)", "blur(0px)", "blur(0px)", "blur(0px)"],
      textShadow: [
        "0 0 0 rgba(255,255,255,0)",
        "0 0 45px rgba(255,255,255,0.85)",
        "0 0 12px rgba(255,255,255,0.3)",
        "0 0 22px rgba(255,255,255,0.55)",
      ],
    }}
    transition={{ delay: 0.15 + i * 0.08, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
    className="inline-block"
  >
    {ch === " " ? "\u00A0" : ch}
  </motion.span>
);

export default function Preloader({ onDone }) {
  const [count, setCount] = useState(0);
  const [cue, setCue] = useState(0);

  useEffect(() => {
    let n = 0;
    const c = setInterval(() => {
      n += Math.floor(Math.random() * 9) + 4;
      if (n >= 100) {
        n = 100;
        clearInterval(c);
      }
      setCount(n);
    }, 70);
    const ct = setInterval(
      () => setCue((v) => Math.min(v + 1, CUES.length - 1)),
      560,
    );
    const t = setTimeout(() => onDone && onDone(), 2900);
    return () => {
      clearInterval(c);
      clearInterval(ct);
      clearTimeout(t);
    };
  }, [onDone]);

  return (
    <motion.div
      initial={{ y: 0 }}
      exit={{ y: "-100%" }}
      transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden bg-black"
      data-testid="preloader"
    >
      {/* two crossed spotlights */}
      <motion.div
        className="absolute -top-1/4 left-[28%] h-[150%] w-[24vw] origin-top blur-[3px]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0.34), rgba(255,255,255,0.02) 55%, transparent 78%)",
        }}
        initial={{ rotate: -24, opacity: 0 }}
        animate={{ rotate: [-24, 10, -6, 4], opacity: [0, 0.95, 0.7, 0.55] }}
        transition={{ duration: 2.8, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -top-1/4 left-[72%] h-[150%] w-[24vw] origin-top blur-[3px]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0.34), rgba(255,255,255,0.02) 55%, transparent 78%)",
        }}
        initial={{ rotate: 24, opacity: 0 }}
        animate={{ rotate: [24, -10, 6, -4], opacity: [0, 0.95, 0.7, 0.55] }}
        transition={{ duration: 2.8, ease: "easeInOut" }}
      />

      {/* horizontal scan line */}
      <motion.div
        className="absolute inset-x-0 h-px bg-white/45"
        initial={{ top: "-2%", opacity: 0.9 }}
        animate={{ top: ["-2%", "102%"], opacity: [0.9, 0.9, 0] }}
        transition={{ duration: 2.9, ease: "linear" }}
      />

      {/* corner bracket ticks */}
      {[
        "top-6 left-6 border-t border-l",
        "top-6 right-6 border-t border-r",
        "bottom-6 left-6 border-b border-l",
        "bottom-6 right-6 border-b border-r",
      ].map((c, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.35 }}
          transition={{ delay: 0.4 + i * 0.08, duration: 0.5 }}
          className={`absolute h-6 w-6 border-white/40 ${c}`}
        />
      ))}

      {/* wordmark */}
      <div className="relative z-10 text-center px-6">
        <div className="font-display text-[13vw] sm:text-8xl lg:text-9xl font-extrabold uppercase tracking-tighter text-chrome flex justify-center leading-none">
          {"VISION".split("").map((ch, i) => (
            <Letter key={"v" + i} ch={ch} i={i} />
          ))}
        </div>
        <div className="mt-4 font-display text-[5.2vw] sm:text-3xl lg:text-5xl font-bold uppercase tracking-[0.35em] sm:tracking-[0.5em] text-zinc-200 flex justify-center leading-none">
          {"STUDIO".split("").map((ch, i) => (
            <Letter key={"s" + i} ch={ch} i={i + 7} />
          ))}
        </div>
      </div>

      {/* base bright flare */}
      <motion.div
        className="pointer-events-none absolute left-1/2 top-[68%] h-56 w-56 -translate-x-1/2 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.55), rgba(255,255,255,0.05) 40%, transparent 70%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.5, 1], opacity: [0, 1, 0.55] }}
        transition={{ delay: 1.0, duration: 1.1 }}
      />

      {/* progress + cue */}
      <div className="relative z-10 mt-24 flex flex-col items-center gap-4">
        <div className="flex items-center gap-4">
          <span className="text-[10px] uppercase tracking-[0.35em] text-zinc-600">Cueing</span>
          <span className="relative h-px w-40 overflow-hidden bg-zinc-800">
            <span
              className="absolute inset-0 origin-left bg-white"
              style={{ transform: `scaleX(${count / 100})` }}
            />
          </span>
          <span
            className="font-display text-sm tabular-nums text-zinc-400"
            data-testid="preloader-count"
          >
            {String(count).padStart(3, "0")}
          </span>
        </div>
        <div className="h-4 overflow-hidden">
          <motion.div
            animate={{ y: -cue * 16 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center"
          >
            {CUES.map((c, i) => (
              <span
                key={i}
                className="h-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500"
              >
                {c}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
